// 花嫁の絵を「騎士の線」(docs/rules/art-style.md) で描き直した案A (2026-10-25 見本・10-26 から配布版の既定)。
//   既定 (配布版・開発版とも) は a。開発版だけ ?brideArt=old で元の絵、?brideArt=b|c で立ち姿 (idle) の1枚だけを別の案へ替える。
//   a = 太い輪郭・平らな3段 (推奨)／b = 中くらいの輪郭・3段／c = 太い輪郭・2段。
// 作者の言葉 (10-23)「他の絵も全部統一していかないと。」・進め方 (10-21)「一旦1枚だけ統一したデザインで書いて見せてもらってから判断する」。
// 大きさ・横の位置は sprites.ts の POSE_SCALE_FIX.bride と BRIDE_REDRAW_ANCHOR_X (ファイルの幹で引く・頭のてっぺん→腰の黒い薔薇を元の絵と同じ長さ・薔薇を同じ所に)。
// 10-25 作者の答え S-1「この線で全部進めてよい」・S-2「A」→ a の時は剣舞の絵 (前向き6枚・回る途中の3枚) も
//   案A の線で描き直した *_art_1025_a へ替える (b・c は立ち姿だけ)。記録 = docs/research/2026-10-25-bride-flurry-art-a.md
//   同じく a の時は、いつも見える状態の絵 (構え・攻撃・弾かれ・崩れ・倒れ) も案A へ替える。記録 = docs/research/2026-10-25-bride-state-art-a.md
//   同じく a の時は、技ごとの絵 (ヴェール・招待・茨の道・とどめ) も案A へ替える。記録 = docs/research/2026-10-25-bride-move-art-a.md
//   10-26 残りの4枚 (返しの予告・三重の返礼の溜め・抱擁) も足した。記録 = docs/research/2026-10-26-bride-art-all.md
// 10-26 作者「今まで騎士の線でかけてなかったから全部置き換えるくらいのつもりで」→ 全部そろえて配布版の既定を a にした。
// 配布版は URL を読まない (常に a。開発版だけ old/b/c を読む)。
// 記録 = docs/research/2026-10-25-bride-art-sample.md

import { clearBossSpriteCache, setSpriteFileOverride } from './sprites'
import { BRIDE_FLOOR_KEYS, brideRedrawParts, preloadBrideHugFront } from './bride_redraw_1023'
import { setBrideTurnArtSet } from './bride_spin_fx'
import type { SpriteKey } from './types'

export type BrideArtSample = 'a' | 'b' | 'c'

export const BRIDE_ART_SAMPLE_STEMS: Readonly<Record<BrideArtSample, string>> = {
  a: 'idle_art_1025_a',
  b: 'idle_art_1025_b',
  c: 'idle_art_1025_c',
}

/** URL の値を案へ。配布版は常に a。開発版は old/off/0 で元の絵 (null)、b・c はそのまま、それ以外は a。 */
export function parseBrideArtSample(raw: string | null, dev: boolean = import.meta.env.DEV): BrideArtSample | null {
  if (!dev) return 'a'
  const t = (raw ?? '').trim()
  if (t === 'old' || t === 'off' || t === '0') return null
  if (t === 'b' || t === 'c') return t
  return 'a'
}

/** 剣舞の前向きの絵: キー → 案A の線で描き直した絵の幹。回る途中の3枚は bride_spin_fx.ts の BRIDE_TURN_ART_LINE_A_1025。 */
export const BRIDE_FLURRY_LINE_A_STEMS: Readonly<Record<string, string>> = {
  flurry_charge: 'flurry_charge_art_1025_a',
  flurry_swing_right: 'flurry_swing_right_art_1025_a',
  flurry_swing_cross: 'flurry_swing_cross_art_1025_a',
  flurry_swing_left: 'flurry_swing_left_art_1025_a',
  flurry_slam_windup: 'flurry_slam_windup_art_1025_a',
  flurry_slam: 'flurry_slam_art_1025_a',
}

/** いつも見える状態の絵: キー → 案A の幹。構え (telegraph) は元から立ち姿と同じ絵なので、立ち姿の案A をそのまま使う。 */
export const BRIDE_STATE_LINE_A_STEMS: Readonly<Record<string, string>> = {
  telegraph: 'idle_art_1025_a',
  active: 'active_art_1025_a',
  parried: 'parried_art_1025_a',
  staggered: 'staggered_art_1025_a',
  defeat: 'defeat_art_1025_a',
}

/** 技ごとの絵 (配布版で今使っている物だけ): キー → 案A の幹。
 *  10-26 返しの予告 (side_crouch)・三重の返礼の溜め・抱擁の2枚も足した。動きは変えず絵だけ (抱擁・一閃・返しの動きは答え待ち)。
 *  10-27 配布版になった返しの一閃の間の絵3枚と両腕で抱く絵も足した。開発版だけの比較版 (?brideRedraw の一閃・返し) は入れていない。
 *  とどめの4枚は配布版で *_1023 へ差し替わっている (bride_redraw_1023.ts の setBrideExecArt) ので、外す時はそこへ戻す。 */
export const BRIDE_MOVE_LINE_A_STEMS: Readonly<Record<string, string>> = {
  veil_coil: 'veil_coil_art_1025_a',
  veil_twist: 'veil_twist_art_1025_a',
  veil_sweep_contact: 'veil_sweep_contact_art_1025_a',
  veil_follow: 'veil_follow_art_1025_a',
  veil_open: 'veil_open_art_1025_a',
  invitation_prep: 'invitation_prep_art_1025_a',
  invitation_reach: 'invitation_reach_art_1025_a',
  invitation_full: 'invitation_full_art_1025_a',
  aisle_rise: 'aisle_rise_art_1025_a',
  aisle_kneel: 'aisle_kneel_art_1025_a',
  exec_found_1009: 'exec_found_art_1025_a',
  exec_glide_1009: 'exec_glide_art_1025_a',
  exec_coil_1009: 'exec_coil_art_1025_a',
  exec_okaeri_1009: 'exec_okaeri_art_1025_a',
  exec_parried_snap_1012: 'exec_parried_snap_art_1025_a',
  exec_cover: 'exec_cover_art_1025_a',
  side_crouch: 'side_crouch_art_1025_a',
  blink_triple_charge_1021: 'blink_triple_charge_1021_art_1025_a',
  embrace_open: 'embrace_open_art_1025_a',
  embrace_close: 'embrace_close_art_1025_a',
  // 10-27 返しの一閃の間の絵3枚と両腕で抱く絵 (配布版になったので案A へ)。抱く絵の腕だけの重ね絵は同じ幹の _front (bride_redraw_1023.ts)。
  kaeshi_tween_rise_1023: 'kaeshi_tween_rise_1023_art_1025_a',
  kaeshi_tween_reach_1023: 'kaeshi_tween_reach_1023_art_1025_a',
  kaeshi_tween_settle_1023: 'kaeshi_tween_settle_1023_art_1025_a',
  embrace_hug_1023: 'embrace_hug_1023_art_1025_a',
}

/** 剣舞の絵を案A へ替える／外す。外す時は ?brideRedraw=floor の床版が入っていればそれへ戻す。 */
function setBrideFlurryLineA(on: boolean): void {
  const floor = brideRedrawParts().has('floor')
  for (const [k, stem] of Object.entries(BRIDE_FLURRY_LINE_A_STEMS)) {
    const back = floor && BRIDE_FLOOR_KEYS.includes(k) ? `${k}_floor_1023` : null
    setSpriteFileOverride('bride', '', k as SpriteKey, on ? stem : back)
  }
  setBrideTurnArtSet(on ? 'lineA' : floor ? 'floor' : 'base')
  for (const [k, stem] of Object.entries(BRIDE_STATE_LINE_A_STEMS)) setSpriteFileOverride('bride', '', k as SpriteKey, on ? stem : null)
  for (const [k, stem] of Object.entries(BRIDE_MOVE_LINE_A_STEMS)) {
    const back = k.endsWith('_1009') ? k.replace('_1009', '_1023') : null
    setSpriteFileOverride('bride', '', k as SpriteKey, on ? stem : back)
  }
}

let current: BrideArtSample | null = null
export function brideArtSample(): BrideArtSample | null { return current }

export function setBrideArtSample(raw: string | null, dev: boolean = import.meta.env.DEV): void {
  const next = parseBrideArtSample(raw, dev)
  if (next === current) return
  const flurryWas = current === 'a'
  current = next
  setSpriteFileOverride('bride', '', 'idle' as SpriteKey, next ? BRIDE_ART_SAMPLE_STEMS[next] : null)
  if (flurryWas !== (next === 'a')) setBrideFlurryLineA(next === 'a')
  clearBossSpriteCache('bride')
  preloadBrideHugFront()
}
